/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'

import SelectionSummaryAccordion from './wizard/stepTwo/SelectionSummaryAccordion'

type Props = {
  selectedProjects: AnyProject[]
  projectName: string
  isUnlinking: boolean
  onCancel: () => void
  onConfirm: () => void
}

const UnlinkProjectsModal: React.FunctionComponent<Props> = ({
  selectedProjects,
  projectName,
  isUnlinking,
  onCancel,
  onConfirm,
}) => (
  <EuiModal
    onClose={onCancel}
    aria-labelledby='unlinkProjectsModalTitle'
    data-test-subj='cps-unlinkProjectsModal'
  >
    <EuiModalHeader>
      <EuiModalHeaderTitle id='unlinkProjectsModalTitle'>
        <FormattedMessage
          id='linkedProjects.unlinkModal.title'
          defaultMessage='Unlink the selected {count, plural, one {project} other {projects}}?'
          values={{ count: selectedProjects.length }}
        />
      </EuiModalHeaderTitle>
    </EuiModalHeader>

    <EuiModalBody>
      <EuiText>
        <p>
          <FormattedMessage
            id='linkedProjects.unlinkModal.description'
            defaultMessage='The following {count, plural, one {project} other {projects}}  will be unlinked from {projectName}. When you unlink a project, its data is no longer returned in queries and visualizations.'
            values={{
              count: selectedProjects.length,
              projectName: (
                <EuiText color='accent' component='span'>
                  {projectName}
                </EuiText>
              ),
            }}
          />
        </p>
      </EuiText>

      <EuiSpacer size='m' />
      <SelectionSummaryAccordion selectedProjects={selectedProjects} />
    </EuiModalBody>
    <EuiModalFooter>
      <EuiButtonEmpty
        onClick={onCancel}
        size='s'
        data-test-subj='cps-unlinkProjectsModal-cancelButton'
      >
        <FormattedMessage id='linkedProjects.unlinkModal.cancel' defaultMessage='Cancel' />
      </EuiButtonEmpty>
      <EuiButton
        color='danger'
        onClick={onConfirm}
        fill={true}
        isLoading={isUnlinking}
        data-test-subj='cps-unlinkProjectsModal-unlinkButton'
      >
        <FormattedMessage id='linkedProjects.unlinkModal.unlink' defaultMessage='Unlink' />
      </EuiButton>
    </EuiModalFooter>
  </EuiModal>
)

export default UnlinkProjectsModal
